import { useCallback, useState } from "react";
import { Close, Create } from "@mui/icons-material";
import { Box, Link, Stack, Typography } from "@mui/material";
import { Button } from "@covertree/ui";
import { formatCurrency } from "@covertree/utils";
import { MineSubsidenceScheduledAddOrEditOtherStructure } from "./MineSubsidenceScheduledAddOrEditOtherStructure";
import { ModalTitle, OtherStructureWithPrice } from "./types";

interface MineSubsidenceScheduledModalContentProps {
  addOrUpdateItem(value: OtherStructureWithPrice): void;
  closeModal(): void;
  deleteItem(otherStructure: OtherStructureWithPrice): number;
  fields: OtherStructureWithPrice[];
  readonly?: boolean;
  updateModalTitle(title: ModalTitle): void;
}

function MineSubsidenceScheduledModalContent({
  addOrUpdateItem,
  closeModal,
  deleteItem,
  fields,
  updateModalTitle,
}: MineSubsidenceScheduledModalContentProps) {
  const [isFormOpen, setIsFormOpen] = useState(!fields.length);
  const [selectedItem, setSelectedItem] = useState<
    OtherStructureWithPrice | undefined
  >();

  const handleEditItem = useCallback(
    (item: OtherStructureWithPrice) => {
      setSelectedItem(item);
      setIsFormOpen(true);
      updateModalTitle(ModalTitle.LimitAndDescription);
    },
    [updateModalTitle]
  );

  const handleAddNewItem = useCallback(() => {
    setSelectedItem(undefined);
    setIsFormOpen(true);
    updateModalTitle(ModalTitle.LimitAndDescription);
  }, [updateModalTitle]);

  const handleRemoveItem = useCallback(
    (item: OtherStructureWithPrice) => {
      const newLength = deleteItem(item);
      if (newLength === 0) {
        setIsFormOpen(true);
        updateModalTitle(ModalTitle.LimitAndDescription);
      } else {
        updateModalTitle(ModalTitle.OtherStructureRemoved);
      }
    },
    [deleteItem, updateModalTitle]
  );

  const changeFormVisibilityAndCloseModal = useCallback(
    (shouldClose: boolean) => {
      if (fields.length === 0 && shouldClose) {
        closeModal();
      }
      setIsFormOpen(false);
    },
    [closeModal, fields]
  );

  return (
    <Box width="100%" data-testid="modal-content">
      {isFormOpen ? (
        <MineSubsidenceScheduledAddOrEditOtherStructure
          addOrUpdateItem={addOrUpdateItem}
          changeFormVisibilityAndCloseModal={changeFormVisibilityAndCloseModal}
          currentItem={selectedItem}
          updateModalTitle={updateModalTitle}
        />
      ) : (
        <>
          <Stack spacing={3} m={0}>
            {fields.map((item) => (
              <Box
                sx={(theme) => ({
                  width: "100%",
                  minHeight: theme.spacing(7),
                  backgroundColor: "secondary.contrastText",
                  padding: theme.spacing(2, 2),
                  borderRadius: theme.spacing(1.5),
                })}
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
                key={item.id ?? item.locator}>
                <Typography
                  variant="h5"
                  sx={{
                    overflowWrap: "break-word",
                    maxWidth: "70%",
                  }}>
                  {item.description}{" "}
                  <Typography
                    variant="h5"
                    component="span"
                    sx={(theme) => ({
                      display: "inline-block",
                      color: theme.colors.brown,
                      marginLeft: theme.spacing(1),
                    })}>
                    {formatCurrency(item.value)}
                  </Typography>
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Box>
                    <Typography variant="h5" color="secondary">
                      +{formatCurrency((item.price ?? 0) / 12, 2)}/mo
                    </Typography>
                    <Typography variant="subtitle1" ml={1.5}>
                      ({formatCurrency(item.price ?? 0, 2)}/year)
                    </Typography>
                  </Box>
                  <Link
                    component="button"
                    variant="body2"
                    underline="always"
                    data-testid="edit-other-structure-button"
                    sx={(theme) => ({
                      marginTop: theme.spacing(0.5),
                      color: "info.main",
                    })}
                    onClick={() => handleEditItem(item)}>
                    <Create fontSize="inherit" />
                  </Link>
                  <Link
                    component="button"
                    variant="body2"
                    underline="always"
                    data-testid="remove-other-structure-button"
                    sx={(theme) => ({
                      marginTop: theme.spacing(0.5),
                      color: "info.main",
                    })}
                    onClick={() => handleRemoveItem(item)}>
                    <Close fontSize="inherit" />
                  </Link>
                </Stack>
              </Box>
            ))}
          </Stack>
          <Box mt={3} mb={4}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleAddNewItem}
              data-testid="add-another-item-button">
              Add Other Structure
            </Button>
          </Box>
          <Box>
            <Button onClick={closeModal} data-testid="finish-button">
              Finish
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
}

export { MineSubsidenceScheduledModalContent };
